package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MoneyFormatter
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.domain.model.ConsolidatedMonth
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalFilters
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalPeriod
import io.github.koalaplot.core.bar.DefaultBarPlotGroupedPointEntry
import io.github.koalaplot.core.bar.DefaultBarPosition
import io.github.koalaplot.core.bar.GroupedVerticalBarPlot
import io.github.koalaplot.core.line.LinePlot
import io.github.koalaplot.core.style.LineStyle
import io.github.koalaplot.core.xygraph.CategoryAxisModel
import io.github.koalaplot.core.xygraph.Point
import io.github.koalaplot.core.xygraph.XYGraph
import io.github.koalaplot.core.xygraph.rememberAxisContent
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_consolidated_axis
import portalvv.shared.generated.resources.operational_consolidated_empty
import portalvv.shared.generated.resources.operational_consolidated_expense
import portalvv.shared.generated.resources.operational_consolidated_legend_planned
import portalvv.shared.generated.resources.operational_consolidated_legend_real
import portalvv.shared.generated.resources.operational_consolidated_profit
import portalvv.shared.generated.resources.operational_consolidated_profit_axis
import portalvv.shared.generated.resources.operational_consolidated_result
import portalvv.shared.generated.resources.operational_consolidated_revenue
import kotlin.math.abs
import kotlin.math.round
import kotlin.math.roundToLong

private const val REAL_COLOR_INDEX = 0
private val REAL_COLOR = AppPalette.Primary500
private val PLANNED_COLOR = AppPalette.Warning500
private val CHART_HEIGHT = 260.dp

@Composable
fun ConsolidatedContent() {
    val controller = rememberConsolidatedController()
    val filters = LocalGlobalFilters.current
    val period = LocalGlobalPeriod.current

    LaunchedEffect(controller) {
        controller.init()
    }

    LaunchedEffect(period.selection) {
        controller.applyPeriod(period.selection)
    }

    LaunchedEffect(filters.unit, filters.manager, filters.director, filters.units) {
        controller.applyFilter(filters.selectedUnit, filters.selectedManager, filters.selectedDirector, filters.units)
    }

    when {
        controller.loading ->
            Box(
                modifier = Modifier.fillMaxWidth().height(200.dp),
                contentAlignment = Alignment.Center,
            ) {
                CircularProgressIndicator()
            }
        controller.months.isEmpty() ->
            EmptyState(message = stringResource(Res.string.operational_consolidated_empty))
        else -> ConsolidatedCharts(controller.months)
    }
}

@Composable
private fun ConsolidatedCharts(months: List<ConsolidatedMonth>) {
    val labels = months.map { MonthCalendar.shortLabel(it.period) }
    val revenueReal = months.map { it.realRevenueCents.toFloat() / 100f }
    val revenuePlanned = months.map { it.plannedRevenueCents.toFloat() / 100f }
    val expenseReal = months.map { it.realExpenseCents.toFloat() / 100f }
    val expensePlanned = months.map { it.plannedExpenseCents.toFloat() / 100f }
    val resultReal = months.map { it.realResultCents.toFloat() / 100f }
    val resultPlanned = months.map { it.plannedResultCents.toFloat() / 100f }
    val profitReal = months.map { it.realProfitPercent.toFloat() }
    val profitPlanned = months.map { it.plannedProfitPercent.toFloat() }

    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
        BarComparisonCard(
            title = stringResource(Res.string.operational_consolidated_revenue),
            labels = labels,
            realValues = revenueReal,
            plannedValues = revenuePlanned,
            modifier = Modifier.fillMaxWidth(),
        )
        BarComparisonCard(
            title = stringResource(Res.string.operational_consolidated_expense),
            labels = labels,
            realValues = expenseReal,
            plannedValues = expensePlanned,
            modifier = Modifier.fillMaxWidth(),
        )
        BarComparisonCard(
            title = stringResource(Res.string.operational_consolidated_result),
            labels = labels,
            realValues = resultReal,
            plannedValues = resultPlanned,
            modifier = Modifier.fillMaxWidth(),
        )
        LineComparisonCard(
            title = stringResource(Res.string.operational_consolidated_profit),
            labels = labels,
            realValues = profitReal,
            plannedValues = profitPlanned,
            percent = true,
            modifier = Modifier.fillMaxWidth(),
        )
    }
}

@Composable
private fun ComparisonCard(
    title: String,
    modifier: Modifier,
    content: @Composable () -> Unit,
) {
    Surface(
        modifier = modifier,
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Text(
                text = title,
                style = MaterialTheme.typography.titleMedium,
            )
            SeriesLegend(
                entries =
                    listOf(
                        LegendEntry(REAL_COLOR, stringResource(Res.string.operational_consolidated_legend_real)),
                        LegendEntry(PLANNED_COLOR, stringResource(Res.string.operational_consolidated_legend_planned)),
                    ),
            )
            content()
        }
    }
}

@Composable
private fun BarComparisonCard(
    title: String,
    labels: List<String>,
    realValues: List<Float>,
    plannedValues: List<Float>,
    modifier: Modifier,
) {
    ComparisonCard(title = title, modifier = modifier) {
        if (labels.isEmpty()) {
            EmptyChart()
        } else {
            val allValues = realValues + plannedValues
            val hi = allValues.maxOrNull() ?: 1f
            val lo = allValues.minOrNull() ?: 0f
            val data =
                labels.mapIndexed { index, label ->
                    DefaultBarPlotGroupedPointEntry(
                        i = label,
                        d =
                            listOf(
                                DefaultBarPosition(0f, realValues[index]),
                                DefaultBarPosition(0f, plannedValues[index]),
                            ),
                    )
                }
            val realLabel = stringResource(Res.string.operational_consolidated_legend_real)
            val plannedLabel = stringResource(Res.string.operational_consolidated_legend_planned)
            XYGraph(
                gridStyle = br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle,
                xAxisModel = CategoryAxisModel(labels),
                yAxisModel = rememberSignedValueAxis(lo * 1.2f, hi * 1.2f),
                xAxisContent = rememberAxisContent(labels = { value -> Text(value, style = MaterialTheme.typography.labelSmall) }),
                yAxisContent =
                    rememberAxisContent(
                        title = { Text(stringResource(Res.string.operational_consolidated_axis), style = MaterialTheme.typography.labelSmall) },
                        labels = { value -> Text(money((value * 100).roundToLong()), style = MaterialTheme.typography.labelSmall) },
                    ),
                modifier = Modifier.fillMaxWidth().height(CHART_HEIGHT),
            ) {
                GroupedVerticalBarPlot(
                    data = data,
                    maxBarGroupWidth = 0.6f,
                    bar = { _, seriesIndex, value ->
                        val real = value.d.getOrNull(0)?.end
                        val planned = value.d.getOrNull(1)?.end
                        val seriesValue = value.d.getOrNull(seriesIndex)?.end ?: 0f
                        val tooltip =
                            buildString {
                                append(value.i)
                                append('\n')
                                append(realLabel)
                                append(": ")
                                append(money(((real ?: 0f) * 100).roundToLong()))
                                append('\n')
                                append(plannedLabel)
                                append(": ")
                                append(money(((planned ?: 0f) * 100).roundToLong()))
                            }
                        HoverTooltip(text = tooltip) {
                            ValueBar(
                                color = if (seriesIndex == REAL_COLOR_INDEX) REAL_COLOR else PLANNED_COLOR,
                                label = compactMoney((seriesValue * 100).roundToLong()),
                                horizontal = false,
                                negative = seriesValue < 0f,
                            )
                        }
                    },
                )
            }
        }
    }
}

@Composable
private fun LineComparisonCard(
    title: String,
    labels: List<String>,
    realValues: List<Float>,
    plannedValues: List<Float>,
    percent: Boolean,
    modifier: Modifier,
) {
    ComparisonCard(title = title, modifier = modifier) {
        if (labels.isEmpty()) {
            EmptyChart()
        } else {
            val allValues = realValues + plannedValues
            val maxValue = allValues.maxOrNull() ?: 0f
            val minValue = allValues.minOrNull() ?: 0f
            val span = (maxValue - minValue).let { if (it > 0f) it else 1f }
            val pad = span * 0.25f
            val yAxis =
                if (percent) {
                    rememberSignedValueAxis(minValue - pad, maxValue + pad)
                } else {
                    rememberValueAxis(maxValue.coerceAtLeast(1f))
                }
            val realPoints = labels.mapIndexed { index, label -> Point(label, realValues[index]) }
            val plannedPoints = labels.mapIndexed { index, label -> Point(label, plannedValues[index]) }
            val realLabel = stringResource(Res.string.operational_consolidated_legend_real)
            val plannedLabel = stringResource(Res.string.operational_consolidated_legend_planned)
            XYGraph(
                gridStyle = br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle,
                xAxisModel = CategoryAxisModel(labels),
                yAxisModel = yAxis,
                xAxisContent = rememberAxisContent(labels = { value -> Text(value, style = MaterialTheme.typography.labelSmall) }),
                yAxisContent =
                    rememberAxisContent(
                        title = {
                            Text(
                                stringResource(if (percent) Res.string.operational_consolidated_profit_axis else Res.string.operational_consolidated_axis),
                                style = MaterialTheme.typography.labelSmall,
                            )
                        },
                        labels = { value -> Text(lineAxisLabel(value, percent), style = MaterialTheme.typography.labelSmall) },
                    ),
                modifier = Modifier.fillMaxWidth().height(CHART_HEIGHT),
            ) {
                LinePlot(
                    data = realPoints,
                    lineStyle = LineStyle(brush = SolidColor(REAL_COLOR), strokeWidth = 2.dp),
                    symbol = { point ->
                        val index = labels.indexOf(point.x)
                        val above = (realValues.getOrNull(index) ?: 0f) >= (plannedValues.getOrNull(index) ?: 0f)
                        HoverTooltip(text = "$realLabel: ${lineTooltip(point.y, percent)}") {
                            LinePointLabel(lineTooltip(point.y, percent), REAL_COLOR, above = above)
                        }
                    },
                )
                LinePlot(
                    data = plannedPoints,
                    lineStyle = LineStyle(brush = SolidColor(PLANNED_COLOR), strokeWidth = 2.dp),
                    symbol = { point ->
                        val index = labels.indexOf(point.x)
                        val realHigher = (realValues.getOrNull(index) ?: 0f) >= (plannedValues.getOrNull(index) ?: 0f)
                        HoverTooltip(text = "$plannedLabel: ${lineTooltip(point.y, percent)}") {
                            LinePointLabel(lineTooltip(point.y, percent), PLANNED_COLOR, above = !realHigher)
                        }
                    },
                )
            }
        }
    }
}

@Composable
private fun EmptyChart() {
    Text(
        text = "—",
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
    )
}

private fun lineAxisLabel(
    value: Float,
    percent: Boolean,
): String = if (percent) percentText(value.toDouble()) else compactMoney((value * 100).roundToLong())

private fun lineTooltip(
    value: Float,
    percent: Boolean,
): String = if (percent) percentText(value.toDouble()) else compactMoney((value * 100).roundToLong())

private fun money(cents: Long): String = "R$ ${MoneyFormatter.formatCents(cents)}"

private fun percentText(value: Double): String {
    val scaled = round(value * 10).toLong()
    return "${scaled / 10},${abs(scaled % 10)}%"
}
